import { useMutation, useQuery } from '@tanstack/react-query';
import { formatEatDateTime, formatMoney, moneyFromCents } from '@wodi/domain';
import type { EligibilityCheckView, EligibilitySchemeView } from '@wodi/shared';
import { Button, LoadingBlock, Notice, RadioGroup, Select, TextArea, TextField } from '@wodi/ui';
import { useEffect, useRef, useState } from 'react';

import { ApiError } from '../api/client.ts';
import {
  checkEligibility,
  recordManualEligibility,
  shaConnectionQuery,
  shaSchemesQuery,
} from '../api/sha.ts';
import { useOnline } from '../hooks/use-online.ts';
import styles from './sha.module.css';

// SHA cover at check-in (docs/plans/phase1-sha.md §9.1; checklist 10, 12). Choosing SHA
// checks at once. Every scheme SHA returns is listed in SHA's words; covered schemes are
// the choices to bill to. Not covered, unreachable, no consent and offline each say what
// happened and offer "Check in as cash". While not connected, a check made on the SHA
// portal can be recorded by hand (plan P1), labelled as such everywhere.

export interface ShaSelection {
  eligibilityCheckId: string;
  schemeId: string;
}

export function ConnectionNote() {
  const connection = useQuery(shaConnectionQuery);
  if (connection.data?.connection !== 'test') return null;
  return <p className={styles.connection}>Test connection (synthetic data)</p>;
}

function limitText(limit: EligibilitySchemeView['limits'][number]): string {
  const money = (cents: string | null) =>
    cents === null ? null : formatMoney(moneyFromCents(BigInt(cents)));
  const remaining = money(limit.remainingCents);
  const total = money(limit.limitCents);
  if (remaining !== null && total !== null) return `${limit.label}: ${remaining} left of ${total}`;
  return `${limit.label}: ${remaining ?? total ?? 'no amount given'}`;
}

export function SchemeList({ check }: { check: EligibilityCheckView }) {
  if (check.schemes.length === 0) return null;
  return (
    <ul className={styles.schemes} aria-label="SHA schemes">
      {check.schemes.map((s) => (
        <li key={s.schemeId} className={styles.scheme}>
          <span className={styles.schemeLine}>{s.line}</span>
          {s.memberNumber === null ? null : (
            <span className={styles.detail}>
              Member number <span className={styles.id}>{s.memberNumber}</span>
            </span>
          )}
          {s.limits.map((l) => (
            <span key={l.label} className={styles.detail}>
              {limitText(l)}
            </span>
          ))}
          {s.reasons.length === 0 ? null : (
            <ul className={styles.reasons} aria-label={`Why ${s.scheme.display} is not covered`}>
              {s.reasons.map((r) => (
                <li key={`${r.system}|${r.code}`}>{r.display}</li>
              ))}
            </ul>
          )}
          {s.needsReview ? (
            <span className={styles.detail}>
              New to this facility: finance sets its prices before SHA amounts show.
            </span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function ManualCheckForm({
  patientId,
  onRecorded,
}: {
  patientId: string;
  onRecorded: (check: EligibilityCheckView) => void;
}) {
  const schemes = useQuery(shaSchemesQuery);
  const [schemeId, setSchemeId] = useState<string | null>(null);
  const [reference, setReference] = useState('');
  const [reason, setReason] = useState('');
  const record = useMutation({
    mutationFn: () =>
      recordManualEligibility(patientId, { schemeId: schemeId ?? '', reference, reason }),
    onSuccess: onRecorded,
  });
  if (schemes.isPending) return <LoadingBlock label="Loading SHA schemes" rows={1} />;
  if (schemes.isError) {
    return (
      <Notice tone="error">
        {schemes.error instanceof ApiError
          ? schemes.error.message
          : "The SHA schemes couldn't be loaded. Try again."}
      </Notice>
    );
  }
  if (schemes.data.items.length === 0) {
    return (
      <Notice tone="info">
        No SHA schemes are set up yet, so a portal check can&apos;t be recorded. Ask finance to add
        them in Settings, under Payers.
      </Notice>
    );
  }
  const ready = schemeId !== null && reference.trim() !== '' && reason.trim().length >= 5;
  return (
    <div className={styles.form} role="group" aria-label="Record a check made on the SHA portal">
      <Select
        label="Scheme the portal showed"
        options={schemes.data.items.map((s) => ({ id: s.id, label: s.name }))}
        placeholder="Choose a scheme"
        value={schemeId}
        onChange={(key) => {
          setSchemeId(key === null ? null : String(key));
        }}
      />
      <TextField label="Portal reference" value={reference} onChange={setReference} />
      <TextArea
        label="Reason"
        description="Why the check was made on the portal, e.g. SHA connection not set up yet."
        value={reason}
        onChange={setReason}
        rows={2}
      />
      {record.isError ? (
        <Notice tone="error">
          {record.error instanceof ApiError
            ? record.error.message
            : "The check couldn't be recorded. Try again."}
        </Notice>
      ) : null}
      <div className={styles.actions}>
        <Button
          isDisabled={!ready}
          isPending={record.isPending}
          pendingLabel="Recording…"
          onPress={() => {
            record.mutate();
          }}
        >
          Record portal check
        </Button>
      </div>
    </div>
  );
}

/**
 * Runs the check once when shown, then shows its result. `onSelect` gets the covered
 * scheme to bill to, or null while there is none.
 */
export function ShaCover({
  patientId,
  onSelect,
  onUseCash,
}: {
  patientId: string;
  onSelect: (selection: ShaSelection | null) => void;
  onUseCash: () => void;
}) {
  const online = useOnline();
  const [check, setCheck] = useState<EligibilityCheckView | null>(null);
  const [chosenScheme, setChosenScheme] = useState<string | null>(null);

  const accept = (view: EligibilityCheckView) => {
    setCheck(view);
    const first = view.schemes.find((s) => s.outcome === 'covered');
    const covered = view.outcome === 'answered' && view.summary.state === 'covered';
    setChosenScheme(covered ? (first?.schemeId ?? null) : null);
    onSelect(
      covered && first !== undefined
        ? { eligibilityCheckId: view.id, schemeId: first.schemeId }
        : null,
    );
  };
  const run = useMutation({
    mutationFn: () => checkEligibility(patientId, {}),
    onSuccess: accept,
    onError: () => {
      onSelect(null);
    },
  });

  // Choosing SHA starts the check at once (plan §9.1); once per panel.
  const started = useRef(false);
  useEffect(() => {
    if (started.current || !online) return;
    started.current = true;
    run.mutate();
  }, [online, run]);

  const cash = (
    <Button variant="primary" onPress={onUseCash}>
      Check in as cash
    </Button>
  );
  const again = (
    <Button
      onPress={() => {
        setCheck(null);
        run.mutate();
      }}
    >
      Check again
    </Button>
  );

  let body;
  if (run.isPending) {
    body = <LoadingBlock label="Checking SHA cover…" rows={2} />;
  } else if (check === null && !online) {
    body = (
      <Notice tone="info" action={cash}>
        Checking SHA cover needs a connection. Check in as cash, or wait until you&apos;re back
        online.
      </Notice>
    );
  } else if (run.isError && check === null) {
    body = (
      <Notice tone="error" action={cash}>
        {run.error instanceof ApiError ? run.error.message : "SHA cover couldn't be checked."}
      </Notice>
    );
  } else if (check !== null) {
    const covered = check.schemes.filter((s) => s.outcome === 'covered');
    body = (
      <>
        <p className={styles.detail}>
          {check.mode === 'recorded_by_hand'
            ? `Recorded by hand from the SHA portal, reference `
            : `Checked ${formatEatDateTime(new Date(check.checkedAt))}`}
          {check.mode === 'recorded_by_hand' ? (
            <span className={styles.id}>{check.manualReference}</span>
          ) : null}
        </p>
        <SchemeList check={check} />
        {check.outcome === 'answered' && covered.length > 0 && check.summary.state === 'covered' ? (
          <RadioGroup
            label="Bill to"
            value={chosenScheme ?? ''}
            onChange={(schemeId) => {
              setChosenScheme(schemeId);
              onSelect({ eligibilityCheckId: check.id, schemeId });
            }}
            options={covered.map((s) => ({ id: s.schemeId, label: `Bill to ${s.scheme.display}` }))}
          />
        ) : null}
        {check.outcome === 'answered' && check.summary.state !== 'covered' ? (
          <Notice tone="info" action={cash}>
            {check.summary.state === 'unknown'
              ? `${check.summary.words}. Wodi can't tell whether that means covered. Check in as cash, or check again later.`
              : `${check.summary.words}. The patient can pay as cash, or sort out cover with SHA and come back.`}
          </Notice>
        ) : null}
        {check.outcome === 'not_configured' ? (
          <>
            <Notice tone="info">{check.problem ?? ''}</Notice>
            <ManualCheckForm patientId={patientId} onRecorded={accept} />
            <div className={styles.actions}>{cash}</div>
          </>
        ) : null}
        {check.outcome === 'unavailable' || check.outcome === 'refused' ? (
          <Notice
            tone="error"
            action={
              <span className={styles.actions}>
                {again}
                {cash}
              </span>
            }
          >
            {check.problem ?? ''}
          </Notice>
        ) : null}
      </>
    );
  }

  return (
    <div className={styles.panel} aria-live="polite">
      <ConnectionNote />
      {body}
    </div>
  );
}
